import React from 'react';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { useCall } from '../../context/CallContext';
import { UserAvatar } from '../common/UserAvatar';

export const IncomingCallModal: React.FC = () => {
  const { callState, activeCall, acceptCall, rejectCall } = useCall();

  if (callState !== 'INCOMING_RINGING' || !activeCall) {
    return null;
  }

  const isVideo = activeCall.callType === 'VIDEO';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-sm bg-card border border-border rounded-3xl p-6 shadow-2xl text-center space-y-6 animate-in zoom-in-95 duration-200">
        {/* Caller Avatar with pulsing ring */}
        <div className="relative mx-auto w-20 h-20">
          <div className="absolute inset-0 rounded-3xl bg-primary/30 animate-ping" />
          <UserAvatar
            userId={activeCall.callerId}
            displayName={activeCall.callerName || 'Incoming Caller'}
            avatarUrl={activeCall.callerAvatar}
            size="2xl"
            className="relative shadow-lg ring-4 ring-card"
          />
        </div>

        {/* Caller Info & Call Type */}
        <div className="space-y-1">
          <h3 className="font-bold text-lg text-foreground truncate">{activeCall.callerName || 'Incoming Call'}</h3>
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5 font-medium">
            {isVideo ? <Video className="h-3.5 w-3.5 text-primary" /> : <Phone className="h-3.5 w-3.5 text-primary" />}
            <span>Incoming {isVideo ? 'Video' : 'Audio'} Call...</span>
          </p>
        </div>

        {/* Action Buttons: Decline (Red) and Accept (Green) */}
        <div className="flex items-center justify-center gap-6 pt-2">
          {/* Decline Button */}
          <div className="flex flex-col items-center gap-1.5">
            <button
              onClick={rejectCall}
              title="Decline Call"
              className="w-14 h-14 rounded-2xl bg-destructive/15 text-destructive hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-all duration-150 shadow-md"
            >
              <PhoneOff className="h-6 w-6" />
            </button>
            <span className="text-[11px] font-semibold text-muted-foreground">Decline</span>
          </div>

          {/* Accept Button */}
          <div className="flex flex-col items-center gap-1.5">
            <button
              onClick={acceptCall}
              title="Accept Call"
              className="w-14 h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-all duration-150 shadow-lg shadow-emerald-500/30 scale-105"
            >
              {isVideo ? <Video className="h-6 w-6" /> : <Phone className="h-6 w-6" />}
            </button>
            <span className="text-[11px] font-semibold text-emerald-500">Accept</span>
          </div>
        </div>
      </div>
    </div>
  );
};
